import React, { useCallback } from 'react';
import WebView from 'react-native-webview';
import { AdyenConfigType } from '@/contexts/payments-context';
import {
  AdyenDropinOnChangeData,
  AdyenDropinOnSubmitData,
  getInjectedJS,
  getWebViewTemplate,
} from '@/utils/payments';
import { partnerSupplierAppURL } from '@/utils/environment';
import { StyleSheet } from 'react-native';
import { WebViewMessageEvent } from 'react-native-webview/lib/WebViewTypes';

const ADYEN_DROPIN_MAX_HEIGHT = 663;

type AdyenWebViewMessage =
  | { state: AdyenDropinOnChangeData; type: 'CHANGE' }
  | { state: AdyenDropinOnSubmitData; type: 'SUBMIT' };

interface Props {
  config: AdyenConfigType;
  onChange: (payload: AdyenDropinOnChangeData) => void;
  onSubmit: (payload: AdyenDropinOnSubmitData) => void;
  requireSavedPaymentMethod: boolean;
  webViewRef: React.RefObject<WebView | null>;
}

export const AdyenDropinWebView: React.FC<Props> = ({
  config,
  onChange,
  onSubmit,
  requireSavedPaymentMethod,
  webViewRef,
}) => {
  const injectedJS = getInjectedJS(config, requireSavedPaymentMethod);

  /*
    The WebView will only call postMessage when submitting the dropin form.
    This is controlled by the event handler in getInjectedJS where we match
    the webview's incoming event on the provided ADD_PAYMENT_METHOD_KEY key
    in the continue button. All other webview events are ignored to prevent
    calling onMessage.
  */
  const onMessage = useCallback(
    (event: WebViewMessageEvent) => {
      const payload: AdyenWebViewMessage = JSON.parse(event.nativeEvent.data);

      if (payload.type === 'SUBMIT') {
        onSubmit(payload.state);
      } else if (payload.type === 'CHANGE') {
        onChange(payload.state);
      } else {
        throw new Error(`Adyen webview sent invalid message: ${payload}`);
      }
    },
    [onChange, onSubmit],
  );

  return (
    <WebView
      onMessage={onMessage}
      ref={webViewRef}
      source={{
        baseUrl: __DEV__ ? 'http://localhost:3000' : partnerSupplierAppURL,
        html: getWebViewTemplate(injectedJS),
      }}
      style={styles.webViewContainer}
      testID="adyen-dropin-webview"
    />
  );
};

const styles = StyleSheet.create({
  webViewContainer: {
    /*
      The webview can increase in height after selecting credit card so
      we set the container min height to include the credit card fields.
      The web Dropin component will automatically resize but there are
      known issues with the webview updating its own height.

      This only applies on tablet/large screens since on small screens
      the screen height is set by the PageContainer.
    */
    height: ADYEN_DROPIN_MAX_HEIGHT,
  },
});
